import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Checkbox } from '../ui/checkbox';
import { X, SlidersHorizontal, RotateCcw } from 'lucide-react';
import { formatPrice } from '../../lib/utils';

const CONDITIONS = [
  { id: 'new', name: 'Nuevo' },
  { id: 'used', name: 'Usado' },
  { id: 'certified', name: 'Certificado' },
  { id: 'salvage', name: 'Para reparar' },
];

const YEARS = Array.from({ length: 30 }, (_, i) => 2025 - i);

export const FilterSidebar = ({ 
  filters, 
  onChange, 
  onReset,
  makes = [],
  states = [],
  categories = [],
  className = '' 
}) => {
  const [priceRange, setPriceRange] = useState([filters.price_min || 0, filters.price_max || 500000]);
  const [yearRange, setYearRange] = useState([filters.year_min || 1990, filters.year_max || 2025]);
  const [mileageRange, setMileageRange] = useState([filters.mileage_min || 0, filters.mileage_max || 500000]);

  useEffect(() => {
    setPriceRange([filters.price_min || 0, filters.price_max || 500000]);
    setYearRange([filters.year_min || 1990, filters.year_max || 2025]);
    setMileageRange([filters.mileage_min || 0, filters.mileage_max || 500000]);
  }, [filters]);

  const handlePriceChange = (value) => {
    setPriceRange(value);
  };

  const handlePriceCommit = () => {
    onChange({ price_min: priceRange[0], price_max: priceRange[1] });
  };

  const handleYearChange = (value) => {
    setYearRange(value);
  };

  const handleYearCommit = () => {
    onChange({ year_min: yearRange[0], year_max: yearRange[1] });
  };

  const handleMileageChange = (value) => {
    setMileageRange(value);
  };

  const handleMileageCommit = () => {
    onChange({ mileage_min: mileageRange[0], mileage_max: mileageRange[1] });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== undefined && v !== '' && v !== null).length;

  return (
    <div className={`bg-white border border-border p-4 rounded-md ${className}`} data-testid="filter-sidebar">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-border mb-4">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5" />
          <span className="font-heading text-lg font-bold uppercase">Filtros</span>
          {activeFiltersCount > 0 && (
            <span className="bg-accent text-white text-xs px-2 py-0.5 rounded-full">
              {activeFiltersCount}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="text-muted-foreground hover:text-foreground"
          data-testid="reset-filters-btn"
        >
          <RotateCcw className="h-4 w-4 mr-1" />Restablecer</Button>
      </div>

      <Accordion type="multiple" defaultValue={['category', 'price', 'year', 'location']} className="space-y-2">
        {/* Category */}
        <AccordionItem value="category" className="border-none">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide py-2 hover:no-underline">Categoría</AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <div className="space-y-2">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`cat-${cat.id}`}
                    checked={filters.category === cat.id}
                    onCheckedChange={(checked) => 
                      onChange({ category: checked ? cat.id : undefined })
                    }
                    data-testid={`filter-category-${cat.id}`}
                  />
                  <Label htmlFor={`cat-${cat.id}`} className="text-sm cursor-pointer">
                    {cat.name}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Make */}
        <AccordionItem value="make" className="border-none">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide py-2 hover:no-underline">Marca</AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <Select
              value={filters.make || 'all'}
              onValueChange={(value) => onChange({ make: value === 'all' ? undefined : value })}
            >
              <SelectTrigger className="w-full" data-testid="filter-make-select">
                <SelectValue placeholder="Todas las marcas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las marcas</SelectItem>
                {makes.map((make) => (
                  <SelectItem key={make} value={make}>{make}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AccordionContent>
        </AccordionItem>

        {/* Condition */}
        <AccordionItem value="condition" className="border-none">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide py-2 hover:no-underline">Condición</AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <div className="space-y-2">
              {CONDITIONS.map((cond) => (
                <div key={cond.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`cond-${cond.id}`}
                    checked={filters.condition === cond.id}
                    onCheckedChange={(checked) => 
                      onChange({ condition: checked ? cond.id : undefined })
                    }
                    data-testid={`filter-condition-${cond.id}`}
                  />
                  <Label htmlFor={`cond-${cond.id}`} className="text-sm cursor-pointer">
                    {cond.name}
                  </Label>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Price Range */}
        <AccordionItem value="price" className="border-none">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide py-2 hover:no-underline">Rango de precio</AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <div className="space-y-4">
              <Slider
                value={priceRange}
                min={0}
                max={500000}
                step={5000}
                onValueChange={handlePriceChange}
                onValueCommit={handlePriceCommit}
                className="mt-2"
                data-testid="filter-price-slider"
              />
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{formatPrice(priceRange[0])}</span>
                <span>{formatPrice(priceRange[1])}</span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Year Range */}
        <AccordionItem value="year" className="border-none">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide py-2 hover:no-underline">Rango de año</AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <div className="space-y-4">
              <Slider
                value={yearRange}
                min={1990}
                max={2025}
                step={1}
                onValueChange={handleYearChange}
                onValueCommit={handleYearCommit}
                className="mt-2"
                data-testid="filter-year-slider"
              />
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{yearRange[0]}</span>
                <span>{yearRange[1]}</span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Mileage Range */}
        <AccordionItem value="mileage" className="border-none">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide py-2 hover:no-underline">Kilometraje</AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <div className="space-y-4">
              <Slider
                value={mileageRange}
                min={0}
                max={500000}
                step={10000}
                onValueChange={handleMileageChange}
                onValueCommit={handleMileageCommit}
                className="mt-2"
                data-testid="filter-mileage-slider"
              />
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{mileageRange[0].toLocaleString()} km</span>
                <span>{mileageRange[1].toLocaleString()} km</span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Location */}
        <AccordionItem value="location" className="border-none">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide py-2 hover:no-underline">Ubicación</AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <Select
              value={filters.state || 'all'}
              onValueChange={(value) => onChange({ state: value === 'all' ? undefined : value })}
            >
              <SelectTrigger className="w-full" data-testid="filter-state-select">
                <SelectValue placeholder="Todo el país" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo el país</SelectItem>
                {states.map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AccordionContent>
        </AccordionItem>

        {/* Featured Only */}
        <AccordionItem value="featured" className="border-none">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wide py-2 hover:no-underline">Destacado</AccordionTrigger>
          <AccordionContent className="pt-2 pb-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="featured-only"
                checked={filters.featured === true}
                onCheckedChange={(checked) => 
                  onChange({ featured: checked ? true : undefined })
                }
                data-testid="filter-featured"
              />
              <Label htmlFor="featured-only" className="text-sm cursor-pointer">
                Anuncios destacados
              </Label>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default FilterSidebar;
